import {
  AllCommentsProps,
  BusniessOfficeNote,
} from "../data-model/busniessOfficeNote";
import { ValidateTemplateRequestItem } from "../data-model/templateValidatorModel";
import { BusniessOfficeReportType } from "../enums/busniessOfficeReportType";

/**
 * Extracts and transforms comments of type FINAL into a write-back format.
 * Filters comments to include only those with `BusniessOfficeReportType.FINAL` type,
 * then maps the first matching group's comments into a structured format for posting.
 *
 * @param {AllCommentsProps[]} comments - Array of comment groups to process.
 * @returns {{ note: string; report_status: string; report_identifier: string }[] | undefined}
 *          An array of formatted comments ready for write-back, or undefined if no FINAL comments exist.
 * @example
 * const comments = [{ type: BusniessOfficeReportType.FINAL, comments: [...] }];
 * const result = getCommentsToWriteFromAllComments(comments);
 * // Returns: [{ note: "...", report_status: "...", report_identifier: "..." }, ...]
 */
const getCommentsToWriteFromAllComments = (comments: AllCommentsProps[]) => {
  const getOnlyPostComments = comments.filter(
    (comment) => comment.type === BusniessOfficeReportType.FINAL,
  );

  if (getOnlyPostComments.length === 0) {
    return;
  }

  const allPostComments = getOnlyPostComments[0].comments.map((comment) => {
    return {
      note: comment.writeBackComment ?? "",
      report_status: comment.boStatus ?? "",
      report_identifier: comment.id,
    };
  });

  return allPostComments;
};

/**
 * Builds the array payload for the template validator API from BO notes.
 *
 * @param comments - Array of comment groups from BO notes UI.
 * @param orgId - Organisation identifier.
 * @param orderId - Order identifier.
 * @param authStatus - Current auth-on-file status for the order.
 * @returns ValidateTemplateRequestItem[] for the validate endpoint.
 */
const getValidateTemplatePayloadFromBoNotes = (
  comments: AllCommentsProps[],
  orgId: string,
  orderId: string,
  authStatus: string,
): ValidateTemplateRequestItem[] => {
  const reports = getCommentsToWriteFromAllComments(comments);
  if (!reports || reports.length === 0) return [];
  return reports.map(({ note, report_identifier }) => ({
    template_text: note,
    report_identifier,
    org_id: orgId,
    order_id: orderId,
    auth_status: authStatus,
  }));
};

/**
 * Finds the commentDate for the comment group that contains a comment with the given report id.
 *
 * @param comments - Array of comment groups from BO notes (e.g. allCommentsForUI).
 * @param reportIdentifier - The report id (comment.id) to look up.
 * @returns The commentDate of the group containing that comment, or undefined if not found.
 */
export const getCommentDateByReportId = (
  comments: AllCommentsProps[],
  reportIdentifier: string,
): string | undefined => {
  const group = comments.find((g) =>
    g.comments.some((c) => c.id === reportIdentifier),
  );
  return group?.commentDate;
};

/**
 * Returns visit_date from auth_on_file.reports for the row matching report_identifier.
 */
export const getVisitDateByReportId = (
  reports: BusniessOfficeNote[],
  reportIdentifier: string,
): string | undefined => {
  const target = reportIdentifier.trim();
  const note = reports.find(
    (n) => (n.report_identifier?.trim() ?? "") === target,
  );
  const visit = note?.visit_date?.trim();
  return visit || undefined;
};

export {
  getCommentsToWriteFromAllComments,
  getValidateTemplatePayloadFromBoNotes,
};
